import {
  Box,
  Card,
  CardActions,
  CardContent,
  Chip,
  Divider,
  Stack,
  Typography,
} from "@mui/material"
import { FC } from "react"
import { useTranslation } from "react-i18next"
import { Link as RouterLink } from "react-router-dom"

// Shape of a feature (subset used by the card). We keep it inline to avoid circular deps.
export interface SaFeatureCardData {
  id: string
  name?: string
  refName?: string
  description?: string
  category?: string
  defaultActivated?: boolean
}

export interface SaFeatureCardProps {
  feature: SaFeatureCardData
  to?: string // link destination (defaults to `/sa/features/${id}`)
  maxWidth?: number
}

export const SaFeatureCard: FC<SaFeatureCardProps> = ({
  feature: f,
  to,
  maxWidth = 380,
}) => {
  const { t } = useTranslation()
  const missingConfig = !f.refName || !f.name
  return (
    <Card
      component={RouterLink as any}
      to={to || `/sa/features/${f.id}`}
      variant="outlined"
      sx={{
        maxWidth,
        width: maxWidth,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        textDecoration: "none",
        transition: "box-shadow 0.15s, border-color 0.15s",
        "&:hover": {
          boxShadow: 4,
          borderColor: "primary.main",
        },
      }}
    >
      <CardContent sx={{ flexGrow: 1 }}>
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          justifyContent="space-between"
        >
          <Typography variant="h6" noWrap title={f.name}>
            {f.name || f.id}
          </Typography>
          {f.defaultActivated && (
            <Chip
              size="small"
              color="success"
              label={t("feature.defaultActivated") as string}
            />
          )}
        </Stack>
        <Box pt={1} />
        <Divider />
        <Box pt={1} />
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
          <Typography variant="overline" sx={{ lineHeight: 1 }}>
            {t("feature.refName") || "refName"}
          </Typography>
          <Box
            component="span"
            sx={{
              fontFamily: "monospace",
              bgcolor: "grey.100",
              px: 0.75,
              py: 0.25,
              borderRadius: 1,
              fontSize: "0.7rem",
              color: "text.primary",
              maxWidth: "100%",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
            title={f.refName}
          >
            {f.refName}
          </Box>
        </Stack>
        {f.category && (
          <Chip
            size="small"
            sx={{ mt: 1 }}
            label={t(`feature.category.${f.category}`)}
            variant="outlined"
          />
        )}
        {f.description && (
          <Typography
            variant="body2"
            sx={{ mt: 1 }}
            color="text.secondary"
            title={f.description}
          >
            {f.description.length > 120
              ? f.description.slice(0, 117) + "..."
              : f.description}
          </Typography>
        )}
        {missingConfig && (
          <Stack mt={2}>
            <Chip
              color="warning"
              size="small"
              label={<Typography variant="caption">Missing config</Typography>}
            />
          </Stack>
        )}
      </CardContent>
      <CardActions sx={{ justifyContent: "flex-end", px: 2, pb: 2 }}>
        <Typography variant="caption" color="text.secondary">
          {f.id}
        </Typography>
      </CardActions>
    </Card>
  )
}

export default SaFeatureCard
